/* ==========================================================================
   Early Access Drop — engine
   Decides which of the three states to show, handles the secret-link gate,
   runs the countdowns, and wires the print-size buttons + gallery.

   Why client-side: Shopify's Liquid (server) cannot read cookies or the
   ?key= URL parameter at render time, so the timed states and the secret
   gate are resolved here in the browser. The "coming soon" state is the
   safe default that shows if JS is off or before this script runs.

   Config is injected by snippets/ea-config.liquid as window.EA_CONFIG.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.EA_CONFIG || {};
  var COOKIE = 'ea_access';
  var COOKIE_DAYS = 30;

  /* ---------- small helpers ------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function getCookie(name) {
    return document.cookie.split('; ').reduce(function (acc, c) {
      var p = c.split('=');
      return p[0] === name ? decodeURIComponent(p.slice(1).join('=')) : acc;
    }, '');
  }
  function getParam(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }

  /* ---------- time + state -------------------------------------------- */
  // Builds an absolute instant from "YYYY-MM-DD", "HH:MM" and a "+/-HH:MM"
  // offset, so the countdown is correct for every visitor regardless of
  // their own timezone.
  function toInstant(dateStr, timeStr, offset) {
    if (!dateStr) return null;
    var t = (timeStr && /^\d{1,2}:\d{2}$/.test(timeStr)) ? timeStr : '00:00';
    if (t.length === 4) t = '0' + t; // H:MM -> HH:MM
    var off = /^[+-]\d{2}:\d{2}$/.test(offset) ? offset : '+00:00';
    var iso = dateStr + 'T' + t + ':00' + off;
    var d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  var startAt = toInstant(CFG.startDate, CFG.startTime, CFG.utcOffset);
  var endAt = toInstant(CFG.endDate, CFG.endTime, CFG.utcOffset);

  // Returns 'pre' | 'early' | 'public' from the real clock.
  function computeState() {
    var now = Date.now();
    if (startAt && now < startAt.getTime()) return 'pre';
    if (endAt && now >= endAt.getTime()) return 'public';
    if (startAt && endAt) return 'early';
    // If dates are missing, fail safe to pre-launch.
    return startAt ? 'early' : 'pre';
  }

  // Secret key handling. If ?key= matches, store it. Access is granted when
  // the stored key matches the configured secret.
  function hasValidKey() {
    var fromUrl = getParam('key');
    if (fromUrl) {
      setCookie(COOKIE, fromUrl, COOKIE_DAYS);
      // Clean the key out of the visible URL (history only, no reload).
      if (window.history && window.history.replaceState) {
        var cleanedSearch = window.location.search
          .replace(/([?&])key=[^&#]*/, '$1')
          .replace(/[?&]+$/, '')
          .replace(/^&/, '?');
        if (cleanedSearch === '?') cleanedSearch = '';
        var clean = window.location.pathname + cleanedSearch + window.location.hash;
        window.history.replaceState({}, document.title, clean);
      }
    }
    var stored = getCookie(COOKIE);
    return !!CFG.secretKey && stored === CFG.secretKey;
  }

  // The effective state, honoring the editor preview override.
  function resolveView() {
    var override = CFG.previewState; // 'auto' | 'pre' | 'early' | 'public'
    if (override && override !== 'auto') {
      return { state: override, unlocked: true, forced: true };
    }
    var state = computeState();
    // Once the drop is public, everyone is in.
    if (state === 'public') {
      return { state: 'public', unlocked: true, forced: false };
    }
    // Before public (whether 'pre' or 'early'): anyone holding a valid secret
    // key is let straight into the early-access experience — even BEFORE the
    // start date. This is what makes the newsletter link work the moment it's
    // sent. Everyone without the key still sees "coming soon", so it stays
    // private until launch. CFG.noKeyRequired is the same bypass, minus the
    // key check — used by pages whose URL itself is the secret (see
    // snippets/lp-vip-config.liquid), so it never affects any drop that
    // doesn't explicitly opt in.
    if (CFG.noKeyRequired || hasValidKey()) {
      return { state: 'early', unlocked: true, forced: false };
    }
    return { state: state, unlocked: false, forced: false };
  }

  /* ---------- countdown rendering ------------------------------------- */
  var countdowns = [];
  function registerCountdowns() {
    countdowns = $all('[data-ea-countdown]').map(function (el) {
      return {
        el: el,
        target: el.getAttribute('data-ea-target') === 'start' ? startAt : endAt,
        num: {
          d: $('[data-cd="d"]', el), h: $('[data-cd="h"]', el),
          m: $('[data-cd="m"]', el), s: $('[data-cd="s"]', el)
        }
      };
    });
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function tick() {
    var now = Date.now();
    countdowns.forEach(function (c) {
      if (!c.target) return;
      var diff = Math.max(0, c.target.getTime() - now);
      var s = Math.floor(diff / 1000);
      var d = Math.floor(s / 86400); s -= d * 86400;
      var h = Math.floor(s / 3600); s -= h * 3600;
      var m = Math.floor(s / 60); s -= m * 60;
      if (c.num.d) c.num.d.textContent = pad(d);
      if (c.num.h) c.num.h.textContent = pad(h);
      if (c.num.m) c.num.m.textContent = pad(m);
      if (c.num.s) c.num.s.textContent = pad(s);
    });
    // Auto-flip to public the instant the timer ends (no refresh needed).
    if (!CFG._flipped && endAt && now >= endAt.getTime() && computeState() === 'public') {
      CFG._flipped = true;
      applyView();
    }
  }

  /* ---------- show/hide states --------------------------------------- */
  function applyView() {
    var view = resolveView();
    var root = $('.ea-root');
    if (!root) return;

    $all('.ea-state', root).forEach(function (el) {
      var name = el.getAttribute('data-ea-state'); // pre | early | public
      var show = false;
      if (name === 'public' && view.state === 'public') show = true;
      else if (name === 'early' && view.state === 'early' && view.unlocked) show = true;
      // Anyone in 'early' without a valid key sees the 'pre' (coming soon) state.
      else if (name === 'pre' && (view.state === 'pre' || (view.state === 'early' && !view.unlocked))) show = true;
      el.hidden = !show;
    });

    root.setAttribute('data-ea-ready', 'true');
    root.setAttribute('data-ea-view', view.state + (view.unlocked ? '-unlocked' : ''));
    document.dispatchEvent(new CustomEvent('ea:view', { detail: view }));
  }

  /* ---------- sticky countdown bar on scroll -------------------------- */
  function initStickyBar() {
    var bar = $('.ea-stickybar');
    if (!bar) return;
    var anchor = $('[data-ea-stickybar-after]') || $('.ea-hero');
    function onScroll() {
      var trigger = anchor ? anchor.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
      // Only show while the (early-access) countdown is relevant.
      var root = $('.ea-root');
      var view = (root && root.getAttribute('data-ea-view')) || '';
      bar.classList.toggle('is-visible', trigger && view.indexOf('early-unlocked') === 0);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('ea:view', onScroll);
    onScroll();
  }

  /* ---------- product: edition/size picker + gallery + size compare --- */
  function initProduct() {
    var buy = $('[data-ea-buy]');
    if (!buy) return;

    var select = $('select[name="id"]', buy);
    var priceLive = $('[data-ea-price-live]', buy);
    var atcText = $('[data-ea-atc-text]', buy);
    var mainImg = $('[data-ea-gallery-main] img');
    var thumbs = $all('.ea-gallery__thumb');
    var sizeRect = $('[data-ea-size-rect]');
    var sizeCaption = $('[data-ea-size-caption]');
    var sizeRelative = $('[data-ea-size-relative]');

    // Size buttons only — excludes the Edition pills (Giclée Print /
    // Original), which share the same .ea-print-btn class for visual
    // styling but are handled separately below by selectEdition().
    var SIZE_BTN_SELECTOR = '.ea-print-btn:not(.ea-edition-btn)';
    var sizeBtns = $all(SIZE_BTN_SELECTOR, buy);

    // Each size renders ONCE, even when both formats offer it (e.g. a 36x36
    // original and a 36x36 print) — rendering it twice read as a duplicate
    // option. A button therefore carries per-format data attributes
    // (data-print-variant-id / data-original-variant-id and friends), and
    // the active format decides which one it actually buys. A size the
    // active format doesn't offer has no attribute for it and gets disabled
    // + struck through, which is what keeps the row honest.
    var currentEdition = 'print';
    function variantIdFor(btn, edition) {
      return btn.getAttribute('data-' + edition + '-variant-id');
    }

    // Scale used by the size-comparison SVG: a standard 80" interior door as
    // the reference object, 3px per inch, art positioned on the same floor
    // line as the door so any size reads correctly at a glance.
    var DOOR_H_IN = 80, PX_PER_IN = 3, FLOOR_Y = 248, ART_ZONE_X = 150, ART_ZONE_W = 120;

    function updateSizeCompare(wIn, hIn, label) {
      if (!sizeRect || !wIn || !hIn || isNaN(wIn) || isNaN(hIn)) return;
      var w = wIn * PX_PER_IN, h = hIn * PX_PER_IN;
      sizeRect.setAttribute('width', w);
      sizeRect.setAttribute('height', h);
      sizeRect.setAttribute('x', ART_ZONE_X + (ART_ZONE_W - w) / 2);
      sizeRect.setAttribute('y', FLOOR_Y - h);
      if (sizeCaption && label) sizeCaption.textContent = label;
      if (sizeRelative) {
        var pct = Math.round((hIn / DOOR_H_IN) * 100);
        sizeRelative.textContent = pct >= 95 ? 'nearly the full height' : (pct + '% of the height');
      }
    }

    // Show the gallery image at a given index, syncing the main image and
    // the active thumbnail. Skips gracefully if that image doesn't exist.
    function showImageIndex(index) {
      if (index == null || index === '' || !thumbs.length) return;
      var thumb = thumbs[parseInt(index, 10)];
      if (!thumb) return;
      var full = thumb.getAttribute('data-full');
      var alt = thumb.getAttribute('data-alt') || '';
      if (mainImg && full) { mainImg.src = full; mainImg.alt = alt; }
      thumbs.forEach(function (t) { t.setAttribute('aria-current', t === thumb ? 'true' : 'false'); });
    }

    function selectVariant(id, priceText, imageIndex, wIn, hIn, sizeLabel) {
      if (select) select.value = id;
      if (priceLive && priceText) priceLive.textContent = priceText;
      sizeBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', variantIdFor(b, currentEdition) === id ? 'true' : 'false');
      });
      if (atcText) atcText.textContent = atcText.getAttribute('data-default') || 'Add to cart';
      showImageIndex(imageIndex);
      updateSizeCompare(wIn, hIn, sizeLabel);
    }

    // Buys the given size in whatever format is currently active. Pass
    // imageIndexOverride as '' to leave the gallery alone (used on load, so
    // the hero image stays put).
    function selectSize(btn, imageIndexOverride) {
      var ed = currentEdition;
      selectVariant(
        variantIdFor(btn, ed),
        btn.getAttribute('data-' + ed + '-price'),
        imageIndexOverride !== undefined ? imageIndexOverride : btn.getAttribute('data-' + ed + '-image-index'),
        parseFloat(btn.getAttribute('data-w')),
        parseFloat(btn.getAttribute('data-h')),
        btn.getAttribute('data-size-label')
      );
    }

    sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectSize(btn);
      });
    });

    /* ---- Format picker (Giclée Print / Original — 1 of 1) --------------
       Rather than hiding the sizes the active format doesn't offer (which
       ran into a real bug — the theme's .ea-prints__buttons { display: flex }
       CSS overrides the HTML `hidden` attribute), we disable + visually
       strike them through, so they're still visible for context but
       genuinely impossible to select. */
    var editionBtns = $all('.ea-edition-btn', buy);

    // Applies the enable/disable + pressed state for a format and returns
    // its default size button, without selecting it — the caller decides how
    // to apply that selection.
    function applyEditionState(edition) {
      currentEdition = edition;
      editionBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-ea-edition') === edition ? 'true' : 'false');
      });
      var group = sizeBtns.filter(function (b) { return variantIdFor(b, edition); });
      group.forEach(function (b) { b.disabled = false; });
      sizeBtns.filter(function (b) { return !variantIdFor(b, edition); })
        .forEach(function (b) { b.disabled = true; });
      return group.filter(function (b) { return b.hasAttribute('data-' + edition + '-default'); })[0] || group[0];
    }

    function selectEdition(edition) {
      var btn = applyEditionState(edition);
      if (btn) selectSize(btn);
    }

    editionBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectEdition(btn.getAttribute('data-ea-edition'));
      });
    });

    // Initial state: Print is the default format. Apply its enable/disable
    // + pressed states, but preselect with '' for the image index so the
    // gallery keeps the hero image on load (tapping a size later still
    // swaps to that size's photo) — the size-compare graphic is already
    // server-rendered to match this default, so there's no jump on load.
    var initialBtn = applyEditionState('print');
    if (initialBtn) {
      selectSize(initialBtn, '');
    }

    // Gallery thumbnails — manual browsing still works and overrides the auto image.
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full');
        var alt = thumb.getAttribute('data-alt') || '';
        if (mainImg && full) { mainImg.src = full; mainImg.alt = alt; }
        thumbs.forEach(function (t) { t.setAttribute('aria-current', t === thumb ? 'true' : 'false'); });
      });
    });
  }

  /* ---------- collapsible details (accordion) ------------------------- */
  function initAccordions() {
    $all('.ea-acc__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
      });
    });
  }

  /* ---------- init ---------------------------------------------------- */
  function init() {
    registerCountdowns();
    applyView();
    initStickyBar();
    initProduct();
    initAccordions();
    tick();
    setInterval(tick, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
